export type SettingsSectionId = 'profil' | 'preferences' | 'reseaux-sociaux' | 'notifications' | 'abonnements' | 'membres';

// Les libellés sont des clés de traduction (voir src/app/i18n/translations/fr.ts), affichées via le pipe `t`
export interface SettingsSection {
  id: SettingsSectionId;
  labelKey: string;
  icon: string;
}

export const SETTINGS_SECTIONS: SettingsSection[] = [
  { id: 'profil', labelKey: 'settings.sections.profil', icon: 'person_outline' },
  { id: 'preferences', labelKey: 'settings.sections.preferences', icon: 'settings' },
  { id: 'reseaux-sociaux', labelKey: 'settings.sections.social', icon: 'share' },
  { id: 'notifications', labelKey: 'settings.sections.notifications', icon: 'notifications_none' },
  { id: 'abonnements', labelKey: 'settings.sections.subscriptions', icon: 'mail_outline' },
  { id: 'membres', labelKey: 'settings.sections.members', icon: 'people_outline' },
];

export interface SettingOption {
  key: string;
  labelKey: string;
  hintKey?: string;
}

export interface SettingGroup {
  titleKey: string;
  options: SettingOption[];
}

export const HOME_PAGE_OPTIONS = [
  { value: 'une', labelKey: 'settings.preferences.homePageOptions.une' },
  { value: 'promotionCode', labelKey: 'settings.preferences.homePageOptions.promotionCode' },
];

const preference = (key: string, withHint = false): SettingOption => ({
  key,
  labelKey: 'settings.preferences.options.' + key,
  hintKey: withHint ? 'settings.preferences.hints.' + key : undefined,
});

const preferenceGroup = (id: string, options: SettingOption[]): SettingGroup => ({
  titleKey: 'settings.preferences.groups.' + id,
  options,
});

export const PREFERENCE_GROUPS: SettingGroup[] = [
  preferenceGroup('pagination', [preference('infiniteScroll')]),
  preferenceGroup('widgets', [
    preference('widgetLastActivities'),
    preference('widgetHotDeals'),
    preference('widgetPopularCategories'),
    preference('widgetBestPromoCodes'),
    preference('widgetDiscussions'),
    preference('widgetNewsMain'),
    preference('widgetNewsCategories'),
  ]),
  preferenceGroup('autoSubscribe', [preference('autoSubscribe')]),
  preferenceGroup('adultContent', [preference('adultContent', true)]),
  preferenceGroup('privacy', [
    preference('allowFollow'),
    preference('allowPrivateMessages'),
    preference('allowBrowsingData'),
  ]),
];

export const DEFAULT_PREFERENCES: { [key: string]: boolean | string } = {
  homePage: 'une',
  infiniteScroll: true,
  widgetLastActivities: true,
  widgetHotDeals: true,
  widgetPopularCategories: true,
  widgetBestPromoCodes: true,
  widgetDiscussions: true,
  widgetNewsMain: true,
  widgetNewsCategories: true,
  autoSubscribe: true,
  adultContent: false,
  allowFollow: true,
  allowPrivateMessages: true,
  allowBrowsingData: true,
};

const notification = (key: string): SettingOption => ({ key, labelKey: 'settings.notifications.options.' + key });

// Même libellé pour tous les doublons par e-mail
const emailNotification = (key: string): SettingOption => ({ key, labelKey: 'settings.notifications.emailOption' });

const notificationGroup = (id: string, options: SettingOption[]): SettingGroup => ({
  titleKey: 'settings.notifications.groups.' + id,
  options,
});

export const NOTIFICATION_GROUPS: SettingGroup[] = [
  notificationGroup('browser', [notification('browserMessage'), notification('browserCall')]),
  notificationGroup('general', [notification('generalMention'), emailNotification('generalEmail')]),
  notificationGroup('myDeals', [
    notification('dealVote'),
    notification('dealComment'),
    notification('dealHot'),
    notification('dealInfoAdded'),
    notification('dealBeforeExpiration'),
    notification('dealExpired'),
    notification('dealReactivated'),
    notification('dealBeforeReactivation'),
    notification('dealMaybeExpired'),
    notification('dealStatsReady'),
    emailNotification('dealsEmail'),
  ]),
  notificationGroup('followedDeals', [notification('followedDealComment'), notification('followedDealInfoAdded')]),
  notificationGroup('myComments', [
    notification('commentReaction'),
    notification('commentApproved'),
    notification('commentReply'),
    notification('commentThreadReaction'),
    notification('commentDeletedMerge'),
    notification('commentRemarkable'),
    emailNotification('commentsEmail'),
  ]),
  notificationGroup('myInfo', [notification('infoLiked'), emailNotification('infoEmail')]),
  notificationGroup('club', [
    notification('clubLevelUp'),
    notification('clubRewardsLost'),
    notification('clubCommentUseful'),
    notification('clubWeeklyVotes'),
    notification('clubReferralUsed'),
    emailNotification('clubEmail'),
  ]),
  notificationGroup('badges', [notification('badgeEarned'), notification('badgeSuperPoster'), emailNotification('badgesEmail')]),
  notificationGroup('followedMembers', [notification('followedMemberPost'), emailNotification('followedMembersEmail')]),
  notificationGroup('messages', [emailNotification('messagesEmail')]),
];

// Tout est activé par défaut, sauf les doublons par e-mail (hors messages privés)
export function defaultNotificationSetting(key: string): boolean {
  return key === 'messagesEmail' || !key.endsWith('Email');
}

export const NEWSLETTER_OPTIONS = [
  { value: 'daily', labelKey: 'settings.subscriptions.daily' },
  { value: 'weekly', labelKey: 'settings.subscriptions.weekly' },
];
